/**
 * MCP Marketplace — Browse and install community MCP servers
 *
 * Features:
 * - Browseable card grid of community MCP servers
 * - Category filter, trust level filter, search
 * - Star ratings, install counts, tool previews
 * - One-click install with auth configuration
 * - Installed badge for already-installed servers
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Store, Star, Download, Shield, ShieldCheck,
  Plug, CheckCircle, Lock, Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  MARKETPLACE_CATEGORIES,
  TRUST_LEVEL_STYLES,
  type MarketplaceServer,
  type MarketplaceCategory,
  type TrustLevel,
} from '@/lib/mock-connections-data';
import { useMcpMarketplace, useMcpServers } from '@/lib/use-connections-module';

export const Route = createFileRoute('/connections/marketplace')({
  component: MarketplacePage,
});

type SortOption = 'popular' | 'rating' | 'newest' | 'name';

// Exported so the nested-IA alias route at /connections/mcp/marketplace
// (added round 4 / #228) can render the same component.
export function MarketplacePage() {
  const [search, setSearch] = useState('');
  // Real-data: published system_mcp_servers with marketplace metadata.
  const { data: realCatalog = [] } = useMcpMarketplace();
  // Cross-check installed state against tenant_mcp_installs. Match by
  // systemServerId — slug used to drift when a marketplace listing was
  // republished under a different slug.
  const { data: installedServers = [] } = useMcpServers();
  const installedByServerId = new Set(
    installedServers
      .map((s) => (s as unknown as { systemServerId?: string | null }).systemServerId)
      .filter((id): id is string => typeof id === 'string'),
  );
  const installedNamespaces = new Set(installedServers.map((s) => s.namespace));
  const servers: MarketplaceServer[] = realCatalog.map((s) => ({
    id: s.id,
    name: s.displayName,
    description: s.description,
    author: s.author,
    category: s.marketplaceCategory as MarketplaceCategory,
    trustLevel: s.trustLevel as TrustLevel,
    // toolCount comes from the platform manifest count exposed by the
    // backend if available; otherwise leave at 0 and the UI shows "—".
    toolCount: 0,
    tools: [],
    installCount: s.installCount,
    rating: s.rating,
    reviewCount: s.reviewCount,
    version: `${s.version}.0.0`,
    license: s.license,
    tags: s.marketplaceTags,
    dockerImage: s.dockerImage ?? s.slug,
    requiresAuth: s.requiresAuth,
    // Codex retest #160 — surface backend-rendered label so the install
    // dialog never interpolates the literal word `undefined`.
    authType: s.authType,
    authTypeLabel:
      s.authTypeLabel ??
      (s.requiresAuth
        ? 'Credentials required (managed by the provider)'
        : 'No credentials required'),
    lastUpdated: new Date().toISOString(),
    installed:
      installedByServerId.has(s.id) || installedNamespaces.has(s.slug),
  }));
  const [categoryFilter, setCategoryFilter] = useState<'all' | MarketplaceCategory>('all');
  const [trustFilter, setTrustFilter] = useState<'all' | TrustLevel>('all');
  const [sort, setSort] = useState<SortOption>('popular');
  const [installTarget, setInstallTarget] = useState<MarketplaceServer | null>(null);
  const [detailServer, setDetailServer] = useState<MarketplaceServer | null>(null);

  // Filter and sort
  const filtered = servers
    .filter(s => {
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
      if (trustFilter !== 'all' && s.trustLevel !== trustFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.tags.some(t => t.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      switch (sort) {
        case 'popular': return b.installCount - a.installCount;
        case 'rating': return b.rating - a.rating;
        case 'newest': return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
        case 'name': return a.name.localeCompare(b.name);
        default: return 0;
      }
    });

  // NOTE: backend marketplace install endpoint is not yet implemented.
  const handleInstall = () => {
    if (!installTarget) return;
    toast.info(
      `Installing "${installTarget.name}" from the marketplace is not yet wired. Register the server manually under MCP Servers for now.`,
    );
    setInstallTarget(null);
  };

  // Stats
  const installedCount = servers.filter(s => s.installed).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Store className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-base font-semibold">MCP Marketplace</h2>
          <Badge variant="secondary" className="text-[9px]">{servers.length} available</Badge>
          <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">{installedCount} installed</Badge>
        </div>
        <p className="text-sm text-muted-foreground">Browse available MCP servers. One-click install is not wired in V1; register servers manually from MCP Servers.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search servers, tags..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v as 'all' | MarketplaceCategory)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(MARKETPLACE_CATEGORIES).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={trustFilter} onValueChange={v => setTrustFilter(v as 'all' | TrustLevel)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Trust" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trust Levels</SelectItem>
            <SelectItem value="official">Official</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="community">Community</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={v => setSort(v as SortOption)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Most Popular</SelectItem>
            <SelectItem value="rating">Highest Rated</SelectItem>
            <SelectItem value="newest">Recently Updated</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Server grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(server => (
            <Card
              key={server.id}
              className={cn(
                'hover:border-primary/30 transition-colors cursor-pointer group',
                server.installed && 'border-emerald-500/20'
              )}
              onClick={() => setDetailServer(server)}
            >
              <CardContent className="pt-4 pb-3 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Plug className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{server.name}</p>
                        {server.installed && <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                      </div>
                      <p className="text-[10px] text-muted-foreground">by {server.author}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={cn('text-[9px] shrink-0', TRUST_LEVEL_STYLES[server.trustLevel])}>
                    {server.trustLevel}
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-[11px] text-muted-foreground line-clamp-2">{server.description}</p>

                {/* Rating + Stats — only render real data. b9c-era fake
                    backfill (4.7 / 24 / 132 for every row) was cleaned up
                    in b9e; honest 0s render as "—". */}
                <div className="flex items-center gap-3">
                  {server.reviewCount > 0 ? (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs font-medium">{server.rating.toFixed(1)}</span>
                      <span className="text-[10px] text-muted-foreground">({server.reviewCount})</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">No reviews yet</span>
                  )}
                  {server.installCount > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Download className="h-3 w-3" />
                      <span>{server.installCount.toLocaleString()}</span>
                    </div>
                  )}
                  {server.toolCount > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Plug className="h-3 w-3" />
                      <span>{server.toolCount} tools</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex items-center gap-1 flex-wrap">
                  <Badge variant="outline" className={cn('text-[9px]', MARKETPLACE_CATEGORIES[server.category]?.color)}>
                    {MARKETPLACE_CATEGORIES[server.category]?.label ?? server.category}
                  </Badge>
                  {server.tags.slice(0, 3).map(tag => (
                    <Badge key={tag} variant="secondary" className="text-[9px]">{tag}</Badge>
                  ))}
                  {server.tags.length > 3 && (
                    <span className="text-[9px] text-muted-foreground">+{server.tags.length - 3}</span>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[10px] text-muted-foreground">v{server.version} · {server.license}</span>
                  {server.installed ? (
                    <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                      Installed
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled
                      title="Marketplace one-click install is not wired in V1. Register this server manually under MCP Servers."
                      onClick={e => e.stopPropagation()}
                    >
                      <Download className="h-3 w-3" /> Register manually
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : servers.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Store className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">Marketplace coming soon</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-md">
            No listings are available yet. Published MCP servers will appear here once the platform catalog is populated.
          </p>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
          <Search className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No servers matching your search</p>
          <p className="text-xs text-muted-foreground/70 mt-1">Try different keywords or adjust your filters</p>
        </div>
      )}

      {/* Detail Dialog */}
      {/* C-A11Y cluster (issue #641 CONN-A11Y-003):
          Radix logs "Missing Description or aria-describedby" when
          DialogContent renders without an inner DialogDescription during
          mount/unmount transitions. Lifting DialogTitle and
          DialogDescription OUT of the `{detailServer && ...}` guard
          ensures both Title + Description ids are present on every
          Radix render pass. The visual layout is preserved by
          conditionally rendering the wrapper that uses detailServer
          inside the Header. */}
      <Dialog open={!!detailServer} onOpenChange={open => { if (!open) setDetailServer(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Plug className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  {detailServer?.name ?? 'MCP server details'}
                  {detailServer?.installed && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                </DialogTitle>
                <DialogDescription>
                  {detailServer
                    ? `by ${detailServer.author} · v${detailServer.version}`
                    : 'Details for the selected MCP server.'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {detailServer && (
            <>

              <div className="space-y-4">
                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={cn('text-[9px]', TRUST_LEVEL_STYLES[detailServer.trustLevel])}>
                    {detailServer.trustLevel}
                  </Badge>
                  <Badge variant="outline" className={cn('text-[9px]', MARKETPLACE_CATEGORIES[detailServer.category]?.color)}>
                    {MARKETPLACE_CATEGORIES[detailServer.category]?.label}
                  </Badge>
                  {detailServer.reviewCount > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs font-medium">{detailServer.rating.toFixed(1)}</span>
                      <span className="text-[10px] text-muted-foreground">({detailServer.reviewCount} reviews)</span>
                    </div>
                  )}
                  {detailServer.installCount > 0 && (
                    <span className="text-[10px] text-muted-foreground">{detailServer.installCount.toLocaleString()} installs</span>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground">{detailServer.description}</p>

                {/* Auth requirement — labels are backend-rendered (Codex
                    retest #160). Falls back to 'No credentials required' if
                    the catalog row is missing the metadata. */}
                {detailServer.requiresAuth && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Requires Authentication</p>
                      <p className="text-[10px] text-muted-foreground">
                        {detailServer.authTypeLabel ?? 'Credentials required (managed by the provider)'}
                      </p>
                    </div>
                  </div>
                )}

                <Separator />

                {/* Tools */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">Tools ({detailServer.toolCount})</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {detailServer.tools.map(tool => (
                      <Badge key={tool} variant="secondary" className="text-[9px] font-mono">{tool}</Badge>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5">
                  {detailServer.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-[9px]">
                      <Tag className="h-2.5 w-2.5 mr-0.5" /> {tag}
                    </Badge>
                  ))}
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>License: <span className="text-foreground">{detailServer.license}</span></div>
                  <div>Updated: <span className="text-foreground">{new Date(detailServer.lastUpdated).toLocaleDateString()}</span></div>
                  <div>Docker: <span className="font-mono text-foreground">{detailServer.dockerImage}</span></div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailServer(null)}>Close</Button>
                {detailServer.installed ? (
                  <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1.5">
                    <CheckCircle className="h-3 w-3 mr-1" /> Already Installed
                  </Badge>
                ) : (
                  <Button
                    disabled
                    title="Marketplace one-click install is not wired in V1. Register this server manually under MCP Servers."
                  >
                    <Download className="h-4 w-4 mr-2" /> Register manually
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Install Confirmation Dialog (Codex retest #160 fixes here) */}
      <Dialog open={!!installTarget} onOpenChange={open => { if (!open) setInstallTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Install {installTarget?.name}</DialogTitle>
            <DialogDescription>
              {installTarget?.requiresAuth
                ? `${installTarget.name} requires authentication (${installTarget.authTypeLabel ?? 'managed by the provider'}). Marketplace one-click install with OAuth handoff is not wired yet — see notice below.`
                : 'Marketplace one-click install is not wired in V1. Register this server manually under MCP Servers.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {/* Honesty banner: PR #128 left this dialog with an
                "Install" button that only fires a not-yet-wired toast.
                Surface that intent up-front so the user doesn't enter a
                deployment confirmation flow believing it will deploy. */}
            {installTarget?.requiresAuth && (
              <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/10 border border-amber-500/30">
                <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    Marketplace OAuth install is not yet implemented
                  </p>
                  <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                    Auth = {installTarget.authTypeLabel ?? 'managed by the provider'}. Until F-20
                    lands, register this server manually under MCP Servers
                    after handling provider consent out-of-band.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              <Shield className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Use manual MCP registration for sandbox and resource policy setup</span>
            </div>
            {installTarget && installTarget.toolCount > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <Plug className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{installTarget.toolCount} tools available after manual registration</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Set approval policy during manual registration</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstallTarget(null)}>Cancel</Button>
            {installTarget?.requiresAuth ? (
              // Disabled with explicit tooltip until OAuth install ships
              // (Codex retest #160 — kicks honesty up to the action button
              // itself so users can't fall into a fake-success path).
              <Button
                disabled
                title="Marketplace OAuth install is not yet wired. Register the server manually under MCP Servers."
              >
                <Download className="h-4 w-4 mr-2" /> Requires OAuth setup
              </Button>
            ) : (
              <Button
                disabled
                title="Marketplace one-click install is not wired in V1. Register this server manually under MCP Servers."
                onClick={handleInstall}
              >
                <Download className="h-4 w-4 mr-2" /> Register manually
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
